<template>
  <div class="section-row" :class="{ 'is-locked': locked, 'is-editing': editMode }">
    <header class="section-header">
      <span
        v-if="editMode"
        class="drag-handle section-drag-handle"
        aria-hidden="true"
      >
        <svg viewBox="0 0 16 16" width="14" height="14">
          <circle cx="6" cy="3" r="1.2" fill="currentColor"/>
          <circle cx="10" cy="3" r="1.2" fill="currentColor"/>
          <circle cx="6" cy="8" r="1.2" fill="currentColor"/>
          <circle cx="10" cy="8" r="1.2" fill="currentColor"/>
          <circle cx="6" cy="13" r="1.2" fill="currentColor"/>
          <circle cx="10" cy="13" r="1.2" fill="currentColor"/>
        </svg>
      </span>

      <button
        type="button"
        class="section-header-main"
        @click="$emit('editSection', section)"
      >
        <span class="section-name">{{ displayName }}</span>
        <span class="section-meta">
          <span class="section-slug mono">{{ section.slug }}</span>
          <span
            v-if="sectionSummaryText"
            class="section-summary"
          > · {{ sectionSummaryText }}</span>
        </span>
      </button>

      <button
        v-if="editMode"
        type="button"
        class="row-delete section-delete"
        :title="cardCount > 0 ? 'Section has cards — empty it before deleting' : `Delete section ${section.slug}`"
        @click="$emit('deleteSection', section)"
      >
        <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
          <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
        </svg>
      </button>
    </header>

    <div class="card-list-wrap">
      <p
        v-if="items.length === 0 && !isAddingCard"
        class="section-empty"
      >No items in this section yet.</p>

      <VueDraggable
        v-else-if="items.length > 0"
        v-model="localItems"
        class="card-list"
        :animation="160"
        handle=".drag-handle"
        :disabled="!editMode || readOnly"
        ghost-class="drag-ghost"
        item-key="id"
        :group="`items-section-${section.id}`"
        @end="onCardsReordered"
      >
        <QuestionnaireCardRow
          v-for="it in localItems"
          :key="it.id"
          :item="it"
          :locked="locked"
          :edit-mode="editMode"
          :read-only="readOnly"
          @edit="$emit('editCard', $event)"
          @delete="$emit('deleteCard', $event)"
        />
      </VueDraggable>

      <div v-if="editMode" class="card-list-footer">
        <div v-if="!isAddingCard" class="add-card-row-wrap">
          <button
            type="button"
            class="add-inline-button"
            @click="$emit('requestAddCard', section)"
          >
            <svg class="plus-icon" viewBox="0 0 16 16" aria-hidden="true">
              <path d="M8 3v10M3 8h10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            Add card
          </button>
        </div>

        <form
          v-else
          class="inline-form"
          @submit.prevent="onAddCardSubmit"
        >
          <div class="inline-form-fields">
            <select
              v-model="newCardType"
              class="inline-select"
              :disabled="isSubmittingCard"
              @change="onTypeChanged"
            >
              <option value="question">Question</option>
              <option value="section_complete">Section end</option>
              <option value="info">Info</option>
            </select>
            <select
              v-if="newCardType === 'question'"
              v-model="newCardInputStyle"
              class="inline-select inline-select-mono"
              :disabled="isSubmittingCard"
              :title="`Input style for the new question`"
            >
              <option
                v-for="style in inputStyleOptions"
                :key="style"
                :value="style"
              >{{ style }}</option>
            </select>
            <input
              ref="codeInput"
              v-model="newCardCode"
              type="text"
              class="inline-input"
              :placeholder="codePlaceholder"
              autocomplete="off"
              autocapitalize="characters"
              spellcheck="false"
              :disabled="isSubmittingCard"
              @input="onCardCodeInput"
            >
            <div class="inline-form-actions">
              <button
                type="button"
                class="btn btn-secondary btn-sm"
                :disabled="isSubmittingCard"
                @click="onAddCardCancel"
              >Cancel</button>
              <button
                type="submit"
                class="btn btn-primary btn-sm"
                :disabled="!isCardCodeValid || isSubmittingCard"
              >{{ isSubmittingCard ? 'Saving…' : 'Save' }}</button>
            </div>
          </div>
          <p v-if="cardCodeError" class="inline-form-error">{{ cardCodeError }}</p>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { VueDraggable } from 'vue-draggable-plus';
import QuestionnaireCardRow from './QuestionnaireCardRow.vue';
import {
  pickName,
  cardCountForSection,
  cardTypeCountsInSection,
  suggestCardCode,
  isCardCodeTaken,
  CODE_REGEX,
  INPUT_STYLES,
} from './_data/cards.js';

export default {
  name: 'QuestionnaireSectionRow',
  components: { VueDraggable, QuestionnaireCardRow },
  props: {
    section: { type: Object, required: true },
    // Junction Step 2 cutover: now an array of resolved junction items
    // (shape: { id, item_type, item, section_id, position, ... }) instead
    // of legacy seq_cards rows. Drag-reorder and per-item edit/delete are
    // disabled while readOnly is true (Step 3 lifts the read-only flag).
    items: { type: Array, required: true },
    // Step 2: every item-row in the new path is read-only. Hides the
    // drag handle, disables VueDraggable, and prevents the inline form
    // from opening (Add-card chooser modal handles that flow now).
    readOnly: { type: Boolean, default: true },
    locked: { type: Boolean, default: false },
    editMode: { type: Boolean, default: false },
    isAddingCard: { type: Boolean, default: false },
    // True while the parent's POST /api/seq/sections/:id/cards is in
    // flight. Disables the form's Cancel/inputs/Save and swaps the
    // Save label to "Saving…". DORMANT post-junction-Step-2 — the
    // inline form template is unreachable; left in place for Step 3
    // wiring.
    isSubmittingCard: { type: Boolean, default: false },
    // Server-side error from the create-card API (or null). Same
    // dormancy note as above.
    cardServerError: { type: String, default: null },
  },
  emits: [
    'editSection',
    'deleteSection',
    'editCard',
    'deleteCard',
    'requestAddCard',
    'submitAddCard',
    'cancelAddCard',
    // Bubble up an "I'm typing again" signal so the parent clears
    // cardServerError. Lets the user see their own input rather than a
    // stale 409 message after they've started fixing the code.
    'clearCardServerError',
    'cardsReordered',
  ],
  data() {
    return {
      // Mirror for VueDraggable when (later) we re-enable item reorder.
      // While readOnly is true the draggable is disabled and this mirror
      // is only used as the v-for source.
      localItems: this.items.slice(),
      newCardType: 'question',
      newCardCode: '',
      newCardInputStyle: 'frequency-4',
      lastSuggested: '',
    };
  },
  computed: {
    displayName() {
      return pickName(this.section.name, this.section.slug);
    },
    cardCount() {
      // Items in this section, not legacy seq_cards. cardCountForSection
      // is dormant post-junction-Step-2.
      return (this.items || []).length;
    },
    // Per-type summary line under the section name. Counts by item_type
    // on the items array — the cardsState.cards-based helpers are
    // dormant post-junction-Step-2.
    sectionSummaryText() {
      const counts = { question: 0, content_card: 0, flow_card: 0 };
      for (const it of (this.items || [])) {
        if (counts[it.item_type] !== undefined) counts[it.item_type]++;
      }
      const parts = [];
      if (counts.question > 0) {
        parts.push(`${counts.question} ${counts.question === 1 ? 'question' : 'questions'}`);
      }
      if (counts.content_card > 0) {
        parts.push(`${counts.content_card} ${counts.content_card === 1 ? 'content card' : 'content cards'}`);
      }
      if (counts.flow_card > 0) {
        parts.push(`${counts.flow_card} ${counts.flow_card === 1 ? 'flow card' : 'flow cards'}`);
      }
      return parts.join(' · ');
    },
    inputStyleOptions() {
      return INPUT_STYLES;
    },
    codePlaceholder() {
      const suggestion = suggestCardCode(this.section.questionnaire_slug, this.newCardType);
      return suggestion || 'CODE';
    },
    isCardCodeValid() {
      const code = (this.newCardCode || '').trim();
      if (!code) return false;
      if (!CODE_REGEX.test(code)) return false;
      if (isCardCodeTaken(this.section.questionnaire_slug, code)) return false;
      return true;
    },
    cardCodeError() {
      // Server error wins. After a 409 the local code may still pass
      // client-side validation (slug not in the local store yet, etc.)
      // — the message from the backend is the authoritative one.
      if (this.cardServerError) return this.cardServerError;
      const code = (this.newCardCode || '').trim();
      if (!code) return null;
      if (!CODE_REGEX.test(code)) {
        return 'Code must start with a letter, then UPPERCASE, digits, or underscores (max 32 chars).';
      }
      if (isCardCodeTaken(this.section.questionnaire_slug, code)) {
        return `Code "${code}" is already used by another card in this questionnaire.`;
      }
      return null;
    },
  },
  watch: {
    items: {
      handler(next) {
        // Parent supplies a freshly-ordered list each render; mirror it.
        // Drag is disabled in read-only mode so this is just an
        // initialisation channel for now.
        this.localItems = next.slice();
      },
      deep: false,
    },
    isAddingCard(open) {
      if (open) {
        // Reset and prefill suggestion when the inline form opens.
        this.newCardType = 'question';
        this.newCardInputStyle = 'frequency-4';
        this.newCardCode = suggestCardCode(this.section.questionnaire_slug, 'question');
        this.lastSuggested = this.newCardCode;
        this.$nextTick(() => {
          const el = this.$refs.codeInput;
          if (el && typeof el.focus === 'function') el.focus();
        });
      } else {
        this.newCardCode = '';
        this.newCardType = 'question';
        this.newCardInputStyle = 'frequency-4';
        this.lastSuggested = '';
      }
    },
  },
  methods: {
    onTypeChanged() {
      // If the user hasn't customised the code, swap in the new type's
      // suggestion (Q<N+1> for question, blank otherwise so they can type
      // FRUITS_COMPLETE etc.).
      if (this.newCardCode === '' || this.newCardCode === this.lastSuggested) {
        const next = suggestCardCode(this.section.questionnaire_slug, this.newCardType);
        this.newCardCode = next;
        this.lastSuggested = next;
      }
      if (this.cardServerError) this.$emit('clearCardServerError');
    },
    onCardCodeInput() {
      if (this.cardServerError) this.$emit('clearCardServerError');
    },
    onAddCardSubmit() {
      if (!this.isCardCodeValid) return;
      if (this.isSubmittingCard) return;
      this.$emit('submitAddCard', {
        section: this.section,
        card_type: this.newCardType,
        code: this.newCardCode.trim(),
        // input_style only applies to question cards; the parent ignores
        // it for the other types but we omit it here too to keep the
        // payload honest.
        input_style: this.newCardType === 'question' ? this.newCardInputStyle : null,
      });
    },
    onAddCardCancel() {
      this.$emit('cancelAddCard');
    },
    onCardsReordered() {
      // Junction Step 2: items are read-only and the VueDraggable above
      // is disabled. This handler will not fire unless Step 3 re-enables
      // the drag, at which point it will need to PATCH the junction's
      // order endpoint (not seq_cards). Defensive: read from localItems
      // and short-circuit when nothing meaningful to emit.
      if (!this.localItems || this.localItems.length === 0) return;
      const orderedIds = this.localItems.map(it => it.id);
      this.$emit('cardsReordered', { section: this.section, orderedIds });
    },
  },
};
</script>

<style scoped>
.section-row {
  background: var(--color-bg-card);
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 14px 16px 12px;
  /* Reserve space for the accent rail so rows don't shift when entering
     edit mode. The rail is invisible by default and visible via
     box-shadow inset on .is-editing. */
  transition: box-shadow 0.15s, border-color 0.15s;
}

/* Subtle "this row is live" indicator: an inset 2px accent rail on the
   left edge. Cheaper visual than a real left border because it doesn't
   shift the layout. */
.section-row.is-editing {
  box-shadow: inset 3px 0 0 var(--color-accent);
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.drag-handle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-tertiary);
  cursor: grab;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
}

.drag-handle:active {
  cursor: grabbing;
}

.drag-handle:hover {
  color: var(--color-text-secondary);
}

.section-header-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.section-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.section-header-main:hover .section-name {
  color: var(--color-accent);
  text-decoration: underline;
  text-underline-offset: 3px;
}

.section-meta {
  font-size: 11px;
  color: var(--color-text-tertiary);
  letter-spacing: 0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.section-slug {
  font-family: var(--font-mono);
  color: var(--color-text-tertiary);
}

.section-summary {
  color: var(--color-text-tertiary);
}

.row-delete {
  flex-shrink: 0;
  background: none;
  border: 0.5px solid transparent;
  border-radius: 4px;
  padding: 4px;
  color: var(--color-text-tertiary);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s, background 0.15s, border-color 0.15s;
}

.row-delete:hover {
  color: var(--color-bad);
  background: var(--color-bad-soft);
  border-color: var(--color-bad-soft);
}

.card-list-wrap {
  padding-left: 26px;
}

.card-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 6px;
}

.section-empty {
  font-size: 12px;
  color: var(--color-text-tertiary);
  font-style: italic;
  margin: 0 0 6px;
  padding: 6px 0;
}

.card-list-footer {
  margin-top: 4px;
}

.add-card-row-wrap {
  display: flex;
  justify-content: flex-start;
}

.add-inline-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: 0.5px dashed var(--color-border);
  border-radius: var(--radius-md);
  padding: 7px 14px;
  font-family: inherit;
  font-size: 12px;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
}

.add-inline-button:hover {
  border-color: var(--color-accent);
  color: var(--color-accent);
  background: var(--color-bg-soft);
}

.plus-icon {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
}

.inline-form {
  background: var(--color-bg-soft);
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 10px 12px;
}

.inline-form-fields {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.inline-select,
.inline-input {
  font-family: inherit;
  font-size: 12px;
  padding: 6px 9px;
  border: 0.5px solid var(--color-border);
  border-radius: 4px;
  background: var(--color-bg-card);
  color: var(--color-text-primary);
}

.inline-input {
  font-family: var(--font-mono);
  flex: 1;
  min-width: 120px;
}

.inline-select-mono {
  font-family: var(--font-mono);
}

.inline-input:focus,
.inline-select:focus {
  outline: none;
  border-color: var(--color-accent);
  box-shadow: 0 0 0 2px var(--color-accent-soft);
}

.inline-form-actions {
  display: flex;
  gap: 6px;
  margin-left: auto;
}

.btn {
  padding: 6px 12px;
  border-radius: var(--radius-md);
  font-size: 12px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  border: 0.5px solid var(--color-border);
  transition: opacity 0.15s, background 0.15s, border-color 0.15s;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 12px;
}

.btn-primary {
  background: var(--color-accent);
  color: white;
  border-color: var(--color-accent);
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-accent-dark);
  border-color: var(--color-accent-dark);
}

.btn-secondary {
  background: var(--color-bg-card);
  color: var(--color-text-primary);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--color-bg-soft);
  border-color: var(--color-border-strong);
}

.inline-form-error {
  margin: 8px 0 0;
  font-size: 11px;
  color: #6B1F18;
}

.drag-ghost {
  opacity: 0.4;
  background: var(--color-accent-soft);
}

.is-locked .section-header-main {
  cursor: pointer;
}
</style>
